/**
 * SensorChartRefactored - SOLID Principles Compliant Chart Component
 * 
 * SOLID Principles: All five principles applied
 * Purpose: This is the main orchestrator component that composes all the
 * SOLID principle-compliant services and components to create a maintainable,
 * extensible, and testable sensor data visualization system.
 * 
 * Responsibilities:
 * - Orchestrate data processing, configuration, and rendering services
 * - Manage React state and lifecycle
 * - Coordinate interactions between specialized components
 * - Provide clean, props-based interface to parent components
 * 
 * Context: Replaces the monolithic 669-line SensorChart.js with a clean
 * composition of focused, single-responsibility components that follow
 * SOLID principles for better maintainability and extensibility.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';

// SOLID principle-compliant services and components
import { RechartsChart } from './components/RechartsChart.jsx';
import { EmptyState } from './components/ChartUtilities.jsx';
import { ChartControls } from './components/ChartControls.jsx';
import { LabelUtils } from './LabelUtils.js';

// Custom hooks for separation of concerns
import { useChartData } from './hooks/useChartData.js';
import { useChartConfig } from './hooks/useChartConfig.js';
import { useZoomHandlers } from './hooks/useZoomHandlers.js';

/**
 * Main refactored sensor chart component
 * 
 * @param {Object} props
 * @param {Array} props.rawData - Raw sensor data from InfluxDB
 * @param {Array} props.selectedFields - Field keys selected for visualization
 * @param {string} props.chartAdapter - Chart library adapter to use ('recharts', 'chartjs')
 */
export function SensorChartRefactored({
  rawData = [],
  selectedFields = [],
  perBuoySelectedFields = {},
  chartAdapter = 'recharts',
  selectedBuoys = [],
  onToggleMerge,
  canMerge = false,
  isMerged = false,
  mergedMode = false,
  onFieldsChange,
  onPerBuoyFieldsChange
}) {
  // === Local State ===
  const [showDots, setShowDots] = useState(false);
  const [showDirections, setShowDirections] = useState(false);
  
  // === Custom Hooks ===
  const {
    chartData,
    setChartData,
    hasEverHadData,
    previousChartData,
    originalDataRef
  } = useChartData({
    rawData,
    selectedFields,
    perBuoySelectedFields,
    selectedBuoys,
    mergedMode
  });

  const {
    chartConfig,
    previousChartConfig
  } = useChartConfig({
    selectedFields,
    perBuoySelectedFields,
    selectedBuoys,
    chartData,
    mergedMode
  });

  const {
    isZoomed,
    dragState,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleResetZoom,
    handleZoomChange,
    zoomController
  } = useZoomHandlers({ originalDataRef, chartData });

  // === Directional Field Logic ===
  // Import DIRECTIONAL_FIELDS constant
  const DIRECTIONAL_FIELDS = useMemo(() => [
    'virtual_waves:dir_mean_deg',
    'virtual_wind:wind_dir_avg_deg', 
    'virtual_currents:dir_deg'
  ], []);

  // Map of directional fields to their corresponding measurement fields
  const DIRECTIONAL_FIELD_MAPPINGS = useMemo(() => ({
    'virtual_waves:dir_mean_deg': 'virtual_waves:hs_m',
    'virtual_wind:wind_dir_avg_deg': 'virtual_wind:wind_speed_avg_mps',
    'virtual_currents:dir_deg': 'virtual_currents:speed_mps'
  }), []);

  // Determine which directional fields are available based on selected measurement fields
  const availableDirectionalFields = useMemo(() => {
    const currentFields = mergedMode 
      ? Object.values(perBuoySelectedFields).flat()
      : selectedFields;
      
    return DIRECTIONAL_FIELDS.filter(dirField => {
      const correspondingField = DIRECTIONAL_FIELD_MAPPINGS[dirField];
      return currentFields.includes(correspondingField);
    });
  }, [selectedFields, perBuoySelectedFields, mergedMode, DIRECTIONAL_FIELDS, DIRECTIONAL_FIELD_MAPPINGS]);

  // Check if any directional fields are currently selected
  const selectedDirectionalFields = useMemo(() => {
    const currentFields = mergedMode 
      ? Object.values(perBuoySelectedFields).flat()
      : selectedFields;
      
    return DIRECTIONAL_FIELDS.filter(dirField => currentFields.includes(dirField));
  }, [selectedFields, perBuoySelectedFields, mergedMode, DIRECTIONAL_FIELDS]);

  const hasDirectionalData = availableDirectionalFields.length > 0;
  
  // Initialize showDirections based on whether directional fields are already selected
  useEffect(() => {
    const shouldShowDirections = selectedDirectionalFields.length > 0;
    setShowDirections(shouldShowDirections);
  }, [selectedDirectionalFields.length]);

  // Handle directional field toggle
  const handleToggleDirections = useCallback((showDir) => {
    setShowDirections(showDir);
    
    if (mergedMode && onPerBuoyFieldsChange) {
      // Handle merged mode - modify perBuoySelectedFields
      const updatedPerBuoyFields = { ...perBuoySelectedFields };
      
      selectedBuoys.forEach(buoyId => {
        const currentFields = updatedPerBuoyFields[buoyId] || [];
        let newFields = [...currentFields];
        
        availableDirectionalFields.forEach(dirField => {
          if (showDir && !newFields.includes(dirField)) {
            newFields.push(dirField);
          } else if (!showDir && newFields.includes(dirField)) {
            newFields = newFields.filter(f => f !== dirField);
          }
        });
        
        updatedPerBuoyFields[buoyId] = newFields;
      });
      
      onPerBuoyFieldsChange(updatedPerBuoyFields);
      
    } else if (!mergedMode && onFieldsChange) {
      // Handle single mode - modify selectedFields
      let newSelectedFields = [...selectedFields];
      
      availableDirectionalFields.forEach(dirField => {
        if (showDir && !newSelectedFields.includes(dirField)) {
          newSelectedFields.push(dirField);
        } else if (!showDir && newSelectedFields.includes(dirField)) {
          newSelectedFields = newSelectedFields.filter(f => f !== dirField);
        }
      });
      
      onFieldsChange(newSelectedFields);
    }
  }, [availableDirectionalFields, selectedFields, perBuoySelectedFields, mergedMode, selectedBuoys, onFieldsChange, onPerBuoyFieldsChange]);

  // === Data and Zoom Integration ===
  // Set up zoom controller to update chart data when zoom changes
  const handleZoomChangeWithData = useCallback((zoomData) => {
    const updatedData = handleZoomChange(zoomData);
    setChartData(updatedData);
  }, [handleZoomChange, setChartData]);
  
  // Override the zoom controller's callback to update chart data
  useEffect(() => {
    zoomController.setZoomChangeCallback(handleZoomChangeWithData);
  }, [handleZoomChangeWithData, zoomController]);

  // === Utility Functions ===
  const getFieldLabel = useCallback((fieldKey) => {
    return LabelUtils.getFieldLabel(fieldKey);
  }, []);

  // === Determine what to render ===
  // Use current data if available, otherwise fall back to previous data during transitions
  const displayChartData = chartData.length > 0 ? chartData : 
                          (hasEverHadData && previousChartData.length > 0) ? previousChartData : [];
  
  const displayChartConfig = chartConfig || 
                            (hasEverHadData && previousChartConfig) ? previousChartConfig : null;

  const hasAnyMergedFields = mergedMode
    ? Object.values(perBuoySelectedFields || {}).some(arr => (arr?.length ?? 0) > 0)
    : (selectedFields?.length ?? 0) > 0;

  // === Early Returns ===
  if (!hasAnyMergedFields) {
    // If we have previous data, show it with an overlay message
    if (hasEverHadData && previousChartData.length > 0 && previousChartConfig) {
      return (
        <div style={{ width: '100%' }}>
          <ChartControls
            showDots={showDots}
            onToggleDots={setShowDots}
            onResetZoom={handleResetZoom}
            isZoomed={isZoomed}
            showDirections={showDirections}
            onToggleDirections={handleToggleDirections}
            hasDirectionalData={hasDirectionalData}
          />
          
          <div style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '1rem 2rem',
              borderRadius: '8px',
              border: '2px solid #007bff',
              zIndex: 1000,
              textAlign: 'center',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: '#007bff'
            }}>
              Select fields from the sidebar to display new data
            </div>
            <div style={{ opacity: 0.3 }}>
              <RechartsChart
                chartData={previousChartData}
                chartConfig={previousChartConfig}
                showDots={showDots}
                dragState={dragState}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                getFieldLabel={getFieldLabel}
                mergedMode={mergedMode}
              />
            </div>
          </div>
        </div>
      );
    }
    
    // No previous data, show empty state
    return (
      <EmptyState message="Select sensor fields to display chart data" />
    );
  }

  if (!displayChartConfig) {
    return (
      <EmptyState message="Processing chart configuration..." />
    );
  }

  if (!displayChartData.length) {
    return (
      <EmptyState message="No data available for selected time range and fields" />
    );
  }

  // === Render Chart ===
  return (
    <div style={{ width: '100%' }}>
      <ChartControls
        showDots={showDots}
        onToggleDots={setShowDots}
        onResetZoom={handleResetZoom}
        isZoomed={isZoomed}
        showDirections={showDirections}
        onToggleDirections={handleToggleDirections}
        hasDirectionalData={hasDirectionalData}
      />
      
      <div style={{
        position: 'relative',
        userSelect: dragState.dragging ? 'none' : 'auto'
      }}>
        <RechartsChart
          chartData={displayChartData}
          chartConfig={displayChartConfig}
          showDots={showDots}
          dragState={dragState}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          getFieldLabel={getFieldLabel}
          mergedMode={mergedMode}
        />
      </div>
    </div>
  );
}

export default SensorChartRefactored;